import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { graphql, gql, compose } from 'react-apollo';

import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui/Table';
import { Tabs, Tab } from 'material-ui/Tabs';

import Loader from '../../UI/Loader';

import styles from './TownsList.css';

const townsQuery = gql`
    query TownsQuery($tournamentId: ID!) {
        towns(tournamentId: $tournamentId) {
            _id
            name {
                ru
                local
            }
            participantsCount
            participantsCountFall
            participantsCountSpring
            stats {
                p {
                    junior {
                        registered
                        first
                        second
                        finished
                    }
                    senior {
                        registered
                        first
                        second
                        finished
                    }
                }
                m {
                    junior {
                        registered
                        first
                        second
                        finished
                    }
                    senior {
                        registered
                        first
                        second
                        finished
                    }
                }
                t {
                    junior {
                        registered
                        first
                        second
                        finished
                    }
                    senior {
                        registered
                        first
                        second
                        finished
                    }
                }
                v {
                    junior {
                        registered
                        first
                        second
                        finished
                    }
                    senior {
                        registered
                        first
                        second
                        finished
                    }
                }
            }
        }
    }
`;

function getBackground({ registered, first, second, finished }) {
    if (!registered) {
        return {};
    }

    let background;

    let firstColor, secondColor, finishedColor;

    if (first === registered) {
        firstColor = '#b8e6b8';
    } else if (first > 0) {
        firstColor = '#f2ef78';
    } else {
        firstColor = 'transparent';
    }

    if (second === registered) {
        secondColor = '#b8e6b8';
    } else if (second > 0) {
        secondColor = '#f2ef78';
    } else {
        secondColor = 'transparent';
    }

    if (finished === registered) {
        finishedColor = '#b8e6b8';
    } else if (finished > 0) {
        finishedColor = '#f2ef78';
    } else {
        finishedColor = 'transparent';
    }

    if (registered === finished) {
        background = `linear-gradient(90deg, #73cb74 50%, #73cb74 50%)`;
    } else {
        background = `linear-gradient(90deg, ${firstColor} 33%, ${secondColor} 33%, ${secondColor} 66%, ${finishedColor} 66%`;
    }

    return { background };
}

function getFilter(season) {
    return function(town) {
        return (
            town.stats[season === 'spring' ? 't' : 'p'].junior.registered ||
            town.stats[season === 'spring' ? 't' : 'p'].senior.registered ||
            town.stats[season === 'spring' ? 'v' : 'm'].junior.registered ||
            town.stats[season === 'spring' ? 'v' : 'm'].senior.registered
        );
    };
}

class TownsList extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        data: PropTypes.shape({
            loading: PropTypes.bool.isRequired,
            towns: PropTypes.array,
        }).isRequired,
        history: PropTypes.object.isRequired,
    };

    state = {
        season: 'fall',
    };

    openTown = event => {
        this.props.history.push(
            `/tournaments/${this.props.tournament.number}/towns/${event.currentTarget.dataset.id}/${this.state.season}`
        );
    };

    render() {
        const {
            data: { towns, loading },
            intl: { formatMessage },
            tournament,
        } = this.props;
        const { season } = this.state;

        const oLevelRound = season === 'spring' ? 't' : 'p';
        const aLevelRound = season === 'spring' ? 'v' : 'm';

        if (loading) {
            return <Loader centerX />;
        }

        if (!towns || !towns.length) {
            return (
                <h2>
                    <FormattedMessage id="TownsList.No towns" />
                </h2>
            );
        }

        return (
            <Fragment>
                <Tabs
                    value={season}
                    onChange={season => this.setState({ season })}
                    tabItemContainerStyle={{ background: 'transparent' }}
                >
                    <Tab
                        label={formatMessage({ id: 'OrgHomePage.fall' })}
                        style={{ color: 'rbga(0, 0, 0, 0.87)' }}
                        value="fall"
                    />
                    {Boolean(tournament.t && tournament.v) && (
                        <Tab
                            label={formatMessage({ id: 'OrgHomePage.spring' })}
                            style={{ color: 'rbga(0, 0, 0, 0.87)' }}
                            value="spring"
                        />
                    )}
                </Tabs>

                <Table>
                    <TableHeader
                        displaySelectAll={false}
                        adjustForCheckbox={false}
                        enableSelectAll={false}
                        selectable={false}
                    >
                        <TableRow>
                            <TableHeaderColumn className={styles.townName}>
                                <FormattedMessage id="RegListsList.townName" />
                            </TableHeaderColumn>
                            <TableHeaderColumn className={styles.participants}>
                                <FormattedMessage id="TownsList.participantCount" />
                            </TableHeaderColumn>
                            <TableHeaderColumn className={styles.level}>
                                <FormattedMessage id="TownsList.p.junior" />
                            </TableHeaderColumn>
                            <TableHeaderColumn className={styles.level}>
                                <FormattedMessage id="TownsList.p.senior" />
                            </TableHeaderColumn>
                            <TableHeaderColumn className={styles.level}>
                                <FormattedMessage id="TownsList.m.junior" />
                            </TableHeaderColumn>
                            <TableHeaderColumn className={styles.level}>
                                <FormattedMessage id="TownsList.m.senior" />
                            </TableHeaderColumn>
                        </TableRow>
                    </TableHeader>
                    <TableBody displayRowCheckbox={false} showRowHover>
                        {towns
                            .filter(getFilter(this.state.season))
                            .map(({ name, _id, participantsCountFall, participantsCountSpring, stats }) => (
                                <TableRow
                                    selectable={false}
                                    onClick={this.openTown}
                                    data-id={_id}
                                    className={styles.row}
                                    key={_id}
                                >
                                    <TableRowColumn className={styles.townName}>{name.ru || name.local}</TableRowColumn>
                                    <TableRowColumn className={styles.participants}>
                                        {season === 'fall' ? participantsCountFall : participantsCountSpring}
                                    </TableRowColumn>
                                    <TableRowColumn
                                        className={styles.level}
                                        style={getBackground(stats[oLevelRound].junior)}
                                    >
                                        {!!stats[oLevelRound].junior.registered &&
                                            `${stats[oLevelRound].junior.registered}/${
                                                stats[oLevelRound].junior.first
                                            }/${stats[oLevelRound].junior.second}/${
                                                stats[oLevelRound].junior.finished
                                            }`}
                                    </TableRowColumn>
                                    <TableRowColumn
                                        className={styles.level}
                                        style={getBackground(stats[oLevelRound].senior)}
                                    >
                                        {!!stats[oLevelRound].senior.registered &&
                                            `${stats[oLevelRound].senior.registered}/${
                                                stats[oLevelRound].senior.first
                                            }/${stats[oLevelRound].senior.second}/${
                                                stats[oLevelRound].senior.finished
                                            }`}
                                    </TableRowColumn>
                                    <TableRowColumn
                                        className={styles.level}
                                        style={getBackground(stats[aLevelRound].junior)}
                                    >
                                        {!!stats[aLevelRound].junior.registered &&
                                            `${stats[aLevelRound].junior.registered}/${
                                                stats[aLevelRound].junior.first
                                            }/${stats[aLevelRound].junior.second}/${
                                                stats[aLevelRound].junior.finished
                                            }`}
                                    </TableRowColumn>
                                    <TableRowColumn
                                        className={styles.level}
                                        style={getBackground(stats[aLevelRound].senior)}
                                    >
                                        {!!stats[aLevelRound].senior.registered &&
                                            `${stats[aLevelRound].senior.registered}/${
                                                stats[aLevelRound].senior.first
                                            }/${stats[aLevelRound].senior.second}/${
                                                stats[aLevelRound].senior.finished
                                            }`}
                                    </TableRowColumn>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </Fragment>
        );
    }
}

export default compose(
    graphql(townsQuery, {
        options: ({ tournament }) => ({
            variables: {
                tournamentId: tournament._id,
            },
            fetchPolicy: 'no-cache',
        }),
    }),
    injectIntl
)(TownsList);



// WEBPACK FOOTER //
// ./src/Components/TownsList/TownsList.js